import { Ban, CheckCircle2, Clock, Loader2, MinusCircle, XCircle } from 'lucide-react';

import type { TLWorkflowExecutionErrorDetails } from '@/api/gen/tessera-backend-api.schemas';
import { TLWfExecutionStatus } from '@/api/gen/tessera-backend-api.schemas';

// Statuses where the execution is still occupying the workflow (not yet
// terminal). Shared so the executions panel and the "already running" warning
// agree on what counts as in-progress.
export const NON_TERMINAL_EXECUTION_STATUSES: readonly TLWfExecutionStatus[] = [
  TLWfExecutionStatus.PENDING,
  TLWfExecutionStatus.RUNNING,
];

export const isNonTerminalExecutionStatus = (status: TLWfExecutionStatus): boolean =>
  NON_TERMINAL_EXECUTION_STATUSES.includes(status);

// `borderClass` recolors the resting 1px border; `outlineClass` carries the
// weight of the highlight. Both spell the color out because Tailwind extracts
// class names statically — a built `outline-${color}` would never be generated.
export const EXECUTION_STATUS_CONFIG: Record<
  string,
  { icon: typeof Clock; color: string; label: string; borderClass?: string; outlineClass?: string }
> = {
  PENDING: {
    icon: Clock,
    color: 'text-zinc-400',
    label: 'Pending',
    borderClass: 'border-zinc-500',
    outlineClass: 'outline-zinc-500',
  },
  RUNNING: {
    icon: Loader2,
    color: 'text-blue-400',
    label: 'Running',
    borderClass: 'border-blue-500',
    outlineClass: 'outline-blue-500',
  },
  COMPLETED: {
    icon: CheckCircle2,
    color: 'text-emerald-400',
    label: 'Completed',
    borderClass: 'border-emerald-500',
    outlineClass: 'outline-emerald-500',
  },
  FAILED: {
    icon: XCircle,
    color: 'text-red-400',
    label: 'Failed',
    borderClass: 'border-red-500',
    outlineClass: 'outline-red-500',
  },
  CANCELLED: {
    icon: Ban,
    color: 'text-zinc-500',
    label: 'Cancelled',
    borderClass: 'border-zinc-500',
    outlineClass: 'outline-zinc-500',
  },
  SKIPPED: {
    icon: MinusCircle,
    color: 'text-zinc-500',
    label: 'Skipped',
    borderClass: 'border-zinc-500',
    outlineClass: 'outline-zinc-500',
  },
};

/**
 * Normalizes the three shapes `error_details` arrives in: `{failed_nodes: [...]}` (node
 * failure), `{type, message}` (finalize, workflow-level failure — e.g. an invalid
 * definition), and `{reason, message}` (reconciler / start-failure triage).
 * Returns null when there is nothing displayable.
 */
export function formatExecutionError(details: TLWorkflowExecutionErrorDetails | undefined): string | null {
  if (!details || typeof details !== 'object') return null;

  const failedNodes = details['failed_nodes'];
  if (Array.isArray(failedNodes)) {
    const names = failedNodes
      .filter((n): n is string => typeof n === 'string')
      .map((n) => n.trim())
      .filter((n) => n.length > 0);
    if (names.length > 0) return `Failed at: ${names.join(', ')}`;
  }

  const message = details['message'];
  const rawLabel = details['reason'] ?? details['type'];
  const label = typeof rawLabel === 'string' && rawLabel.trim().length > 0 ? rawLabel : null;
  if (typeof message === 'string' && message.trim().length > 0) {
    return label ? `${label}: ${message}` : message;
  }
  return label;
}
